import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation, useSearchParams, useParams } from "react-router-dom";
import useStore from "../../store/useStore";
import {
  getBankTransferValidationError,
  MAX_BANK_ACCOUNT_NUMBER_LENGTH,
  MAX_BANK_ACCOUNT_HOLDER_LENGTH,
  MAX_BANK_IFSC_CODE_LENGTH,
  MAX_BANK_NAME_LENGTH,
  MIN_UPI_ID_LENGTH,
  MAX_UPI_ID_LENGTH,
  MAX_OTHER_REASON_LENGTH,
  getUpiIdValidationError,
  RETURN_REASON_CODES,
  REFUND_PREFERENCE_OPTIONS,
} from "../../store/slices/returnsSlice";
import toast from "react-hot-toast";
import { buildAssetUrl } from "../../utils/api";
import {
  FaArrowLeft,
  FaUndo,
  FaCheckCircle,
  FaCamera,
  FaTrashAlt,
  FaBoxOpen,
  FaSpinner,
  FaTimesCircle,
  FaShieldAlt,
  FaPlus,
  FaMinus,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaPhoneAlt,
  FaTruck,
  FaClock,
} from "react-icons/fa";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

const REASON_CODE_ENTRIES = Object.entries(RETURN_REASON_CODES);

/* ── Product Thumbnail Helper ── */
const ProductThumb = ({ src, alt }) => {
  const [failed, setFailed] = useState(false);
  const url = buildAssetUrl(src);

  return (
    <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-gray-100 bg-gray-50 shadow-xs">
      {url && !failed ? (
        <img
          src={url}
          alt={alt || "Product"}
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <FaBoxOpen className="h-6 w-6 text-gray-300" />
      )}
    </div>
  );
};

/* ── Helper to extract full delivery address from multiple order shapes ── */
const getFullAddressData = (order) => {
  if (!order) {
    return { name: "", mobile: "", street: "", city: "", state: "", pincode: "", fullText: "" };
  }

  const raw = order.delivery_address ?? order.shipping_address ?? order.address;
  const isObj = raw && typeof raw === "object";

  const name =
    order.delivery_name ||
    (isObj ? (raw.fullName || raw.full_name || raw.name) : null) ||
    order.customer_name ||
    order.user_name ||
    "";

  const mobile =
    order.delivery_mobile ||
    order.delivery_phone ||
    (isObj ? (raw.phone || raw.mobile || raw.contact_number) : null) ||
    order.mobile ||
    order.phone ||
    "";

  const street =
    (typeof raw === "string" ? raw : "") ||
    (isObj ? (raw.address_line || raw.addressLine1 || raw.address_line_1 || raw.address || raw.street || raw.house_no) : "") ||
    order.delivery_address ||
    order.address_line ||
    order.street ||
    order.address_line_1 ||
    "";

  const landmark =
    (isObj ? (raw.addressLine2 || raw.address_line_2 || raw.landmark) : "") ||
    order.delivery_landmark ||
    order.landmark ||
    "";

  const city =
    (isObj ? raw.city : "") ||
    order.delivery_city ||
    order.city ||
    "";

  const state =
    (isObj ? raw.state : "") ||
    order.delivery_state ||
    order.state ||
    "";

  const pincode =
    (isObj ? (raw.pincode || raw.pin_code || raw.postal_code) : "") ||
    order.delivery_pincode ||
    order.pincode ||
    order.pin_code ||
    "";

  const parts = [];
  if (street) parts.push(street);
  if (landmark) parts.push(`Landmark: ${landmark}`);
  if (city) parts.push(city);
  if (state) parts.push(state);
  if (pincode) parts.push(pincode);

  const fullText = parts.length > 0
    ? parts.join(", ")
    : (typeof raw === "string" && raw.trim() ? raw.trim() : "Address details on file with order");

  return {
    name,
    mobile,
    street,
    landmark,
    city,
    state,
    pincode,
    fullText,
  };
};

const RequestReturnPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const params = useParams();

  const {
    isAuthenticated,
    openAuthModal,
    orders,
    getOrderDetails,
    fetchOrders,
    createReturnRequest,
  } = useStore();

  const fileInputRef = useRef(null);

  // Read params / state
  const stateOrder = location.state?.order;
  const stateItem = location.state?.item;
  const targetOrderId =
    searchParams.get("orderId") ||
    params.orderId ||
    stateOrder?.order_id ||
    stateOrder?.id;
  const targetItemId =
    searchParams.get("itemId") ||
    stateItem?.order_item_id ||
    stateItem?.id;

  // Local states
  const [resolvedOrder, setResolvedOrder] = useState(stateOrder || null);
  const [loadingOrder, setLoadingOrder] = useState(!stateOrder && Boolean(targetOrderId));
  const [orderError, setOrderError] = useState("");

  const [selectedReasons, setSelectedReasons] = useState(["DAMAGED_ITEM"]);
  const [otherReasonNote, setOtherReasonNote] = useState("");
  const [selectedItems, setSelectedItems] = useState({});
  const [itemQuantities, setItemQuantities] = useState({});
  const [itemRemarks, setItemRemarks] = useState({});
  const [notes, setNotes] = useState("");
  const [images, setImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Success state holding full API response
  const [submittedReturn, setSubmittedReturn] = useState(null);

  // ── Refund preference state ───────────────────────────────────────────────────
  const [refundPreference, setRefundPreference]       = useState("WALLET");
  const [bankAccountNumber, setBankAccountNumber]     = useState("");
  const [bankIfscCode, setBankIfscCode]               = useState("");
  const [bankAccountHolder, setBankAccountHolder]     = useState("");
  const [bankName, setBankName]                       = useState("");
  const [upiId, setUpiId]                             = useState("");

  // ── 1. Resolve order data ──
  useEffect(() => {
    if (stateOrder) {
      setResolvedOrder(stateOrder);
      setLoadingOrder(false);
      return;
    }

    if (!targetOrderId) {
      setLoadingOrder(false);
      setOrderError("No order selected. Please select a delivered order to request a return.");
      return;
    }

    let isMounted = true;
    const loadOrder = async () => {
      setLoadingOrder(true);
      setOrderError("");
      try {
        // Try finding in store orders first
        const found = orders.find(
          (o) => String(o.order_id ?? o.id) === String(targetOrderId)
        );
        if (found && isMounted) {
          setResolvedOrder(found);
          setLoadingOrder(false);
          return;
        }

        // Fetch order details via API
        if (getOrderDetails) {
          const detail = await getOrderDetails(targetOrderId);
          if (detail && isMounted) {
            setResolvedOrder(detail);
            setLoadingOrder(false);
            return;
          }
        }

        // Fallback: fetch orders list
        if (fetchOrders) {
          const list = await fetchOrders({ limit: 50 });
          const matched = (list || []).find(
            (o) => String(o.order_id ?? o.id) === String(targetOrderId)
          );
          if (matched && isMounted) {
            setResolvedOrder(matched);
            setLoadingOrder(false);
            return;
          }
        }

        if (isMounted) {
          setOrderError(`Order #${targetOrderId} could not be found.`);
          setLoadingOrder(false);
        }
      } catch (err) {
        if (isMounted) {
          setOrderError(err.message || "Failed to load order details.");
          setLoadingOrder(false);
        }
      }
    };

    loadOrder();

    return () => {
      isMounted = false;
    };
  }, [targetOrderId, stateOrder, orders, getOrderDetails, fetchOrders]);

  // ── 2. Initialize selected items once order is loaded ──
  useEffect(() => {
    if (!resolvedOrder) return;
    const orderItems = resolvedOrder.items ?? [];
    if (!orderItems.length) return;

    const initialSelection = {};
    const initialQuantities = {};
    const initialRemarks = {};

    orderItems.forEach((it) => {
      const itId = String(it.order_item_id ?? it.id);
      const isTarget = targetItemId ? String(targetItemId) === itId : false;

      // If specific item was clicked, only check that one; otherwise check first item by default
      initialSelection[itId] = targetItemId ? isTarget : orderItems.indexOf(it) === 0;
      initialQuantities[itId] = 1;
      initialRemarks[itId] = "";
    });

    setSelectedItems(initialSelection);
    setItemQuantities(initialQuantities);
    setItemRemarks(initialRemarks);
  }, [resolvedOrder, targetItemId]);

  // ── Clean up previews on unmount ──
  useEffect(() => {
    return () => {
      previews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [previews]);

  // ── Calculation of estimated refund ──
  const estimatedRefund = useMemo(() => {
    if (!resolvedOrder?.items?.length) return 0;
    return resolvedOrder.items.reduce((sum, it) => {
      const itId = String(it.order_item_id ?? it.id);
      if (!selectedItems[itId]) return sum;
      const price = parseFloat(it.unit_price ?? it.price ?? it.selling_price ?? 0);
      const qty = Number(itemQuantities[itId] ?? 1);
      return sum + price * qty;
    }, 0);
  }, [resolvedOrder, selectedItems, itemQuantities]);

  const selectedCount = useMemo(() => {
    return Object.values(selectedItems).filter(Boolean).length;
  }, [selectedItems]);

  // ── Full address extraction ──
  const addressData = useMemo(() => {
    return getFullAddressData(resolvedOrder);
  }, [resolvedOrder]);

  // ── Item toggle & quantity handlers ──
  const handleToggleItem = (itId) => {
    setSelectedItems((prev) => ({
      ...prev,
      [itId]: !prev[itId],
    }));
  };

  const handleQtyChange = (itId, maxQty, delta) => {
    setItemQuantities((prev) => {
      const current = Number(prev[itId] ?? 1);
      const next = Math.max(1, Math.min(maxQty, current + delta));
      return { ...prev, [itId]: next };
    });
  };

  const handleRemarkChange = (itId, val) => {
    setItemRemarks((prev) => ({
      ...prev,
      [itId]: val,
    }));
  };

  // ── Reason toggle ──
  const toggleReason = (code) => {
    if (code === "OTHER" && selectedReasons.includes(code)) {
      setOtherReasonNote("");
    }
    setSelectedReasons((prev) =>
      prev.includes(code)
        ? prev.length > 1
          ? prev.filter((c) => c !== code)
          : prev // keep at least 1
        : [...prev, code]
    );
  };

  // ── Image upload & remove ──
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    const combined = [...images, ...files].slice(0, 5); // max 5 photos
    const newUrls = combined.map((f) => URL.createObjectURL(f));
    setImages(combined);
    setPreviews(newUrls);
  };

  const handleRemoveImage = (idx) => {
    URL.revokeObjectURL(previews[idx]);
    setImages((prev) => prev.filter((_, i) => i !== idx));
    setPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  // ── Submit return request ──
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedCount) {
      toast.error("Please select at least one item to return");
      return;
    }

    if (!selectedReasons.length) {
      toast.error("Please select at least one reason for return");
      return;
    }

    if (selectedReasons.includes("OTHER")) {
      const noteLength = otherReasonNote.trim().length;
      if (noteLength < 5 || noteLength > MAX_OTHER_REASON_LENGTH) {
        toast.error(`Please describe the other reason in 5 to ${MAX_OTHER_REASON_LENGTH} characters.`);
        return;
      }
    }

    // Validate refund-method-specific fields
    if (refundPreference === "BANK_TRANSFER") {
      const bankValidationError = getBankTransferValidationError({
        bank_account_number: bankAccountNumber,
        bank_ifsc_code: bankIfscCode,
        bank_account_holder: bankAccountHolder,
        bank_name: bankName,
      });
      if (bankValidationError) {
        toast.error(bankValidationError);
        return;
      }
    } else if (refundPreference === "UPI") {
      const upiValidationError = getUpiIdValidationError(upiId);
      if (upiValidationError) { toast.error(upiValidationError); return; }
    }

    const orderId = resolvedOrder?.order_id ?? resolvedOrder?.id;
    if (!orderId) {
      toast.error("Missing order ID");
      return;
    }

    // Build items payload
    const itemsPayload = (resolvedOrder?.items ?? [])
      .filter((it) => {
        const itId = String(it.order_item_id ?? it.id);
        return selectedItems[itId];
      })
      .map((it) => {
        const itId = it.order_item_id ?? it.id;
        const key = String(itId);
        return {
          order_item_id: Number(itId),
          quantity: Number(itemQuantities[key] ?? 1),
          remark: (itemRemarks[key] ?? "").trim(),
        };
      });

    if (!itemsPayload.length) {
      toast.error("No valid items selected for return");
      return;
    }

    setSubmitting(true);
    try {
      const returnData = await createReturnRequest({
        order_id:            orderId,
        items:               itemsPayload,
        return_type:         "RETURN",
        reason_codes:        selectedReasons,
        notes:               [
          selectedReasons.includes("OTHER") ? `Other reason: ${otherReasonNote.trim()}` : "",
          notes.trim(),
        ].filter(Boolean).join("\n\n"),
        images,
        refund_preference:   refundPreference,
        bank_account_number: bankAccountNumber.trim() || undefined,
        bank_ifsc_code:      bankIfscCode.trim().toUpperCase() || undefined,
        bank_account_holder: bankAccountHolder.trim() || undefined,
        bank_name:           bankName.trim() || undefined,
        upi_id:              upiId.trim() || undefined,
      });

      toast.success("Return request submitted successfully!");
      setSubmittedReturn(returnData);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.message || "Failed to submit return request");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Auth guard ──
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-16">
        <div className="mx-auto max-w-md rounded-3xl border border-gray-200 bg-white p-8 text-center shadow-lg">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600 shadow-sm">
            <LockOutlinedIcon style={{ fontSize: 32 }} />
          </div>
          <h1 className="text-2xl font-black text-gray-900">Sign in to Request Return</h1>
          <p className="mt-2 text-sm text-gray-500">
            Please log in with your account to request a return or refund for your delivered order.
          </p>
          <button
            onClick={openAuthModal}
            className="mt-6 w-full rounded-2xl bg-emerald-600 py-3.5 text-sm font-bold text-white shadow-md shadow-emerald-600/25 transition hover:bg-emerald-700 active:scale-95"
          >
            Login / Sign Up
          </button>
          <button
            onClick={() => navigate(-1)}
            className="mt-3 w-full rounded-2xl border border-gray-200 py-3 text-sm font-semibold text-gray-600 transition hover:bg-gray-50"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // ── Loading state ──
  if (loadingOrder) {
    return (
      <div className="min-h-screen bg-gray-50/60 px-4 py-16">
        <div className="mx-auto max-w-4xl text-center">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-md">
            <FaSpinner className="h-8 w-8 animate-spin text-emerald-600" />
          </div>
          <p className="mt-4 text-base font-bold text-gray-800">Loading order details...</p>
          <p className="mt-1 text-sm text-gray-400">Fetching order and item information for return</p>
        </div>
      </div>
    );
  }

  // ── Error state ──
  if (orderError || !resolvedOrder) {
    return (
      <div className="min-h-screen bg-gray-50/60 px-4 py-16">
        <div className="mx-auto max-w-md rounded-3xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-red-50 text-red-500">
            <FaTimesCircle className="h-8 w-8" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Order Not Found</h1>
          <p className="mt-2 text-sm text-gray-500">
            {orderError || "The order could not be retrieved. Please check your order history."}
          </p>
          <button
            onClick={() => navigate("/account/orders")}
            className="mt-6 w-full rounded-2xl bg-emerald-600 py-3 text-sm font-bold text-white shadow-md shadow-emerald-600/20 transition hover:bg-emerald-700 active:scale-95"
          >
            Back to My Orders
          </button>
        </div>
      </div>
    );
  }

  // ── 3. SUCCESS / CONFIRMATION SCREEN (Uses Full API Response) ──
  if (submittedReturn) {
    const retRef = submittedReturn.return_ref ?? submittedReturn.return_request_number ?? `#${submittedReturn.return_id}`;
    const retOrder = submittedReturn.order_number || submittedReturn.order?.order_id || resolvedOrder.order_number;
    const pickup = submittedReturn.pickup_details;
    const refundInfo = submittedReturn.refund_information;
    const timeline = submittedReturn.timeline;
    const statusInfo = submittedReturn.current_status_info;
    const retItems = submittedReturn.items ?? submittedReturn.return_items ?? [];

    return (
      <div className="min-h-screen bg-gray-50/70 pb-20 pt-8">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <div className="overflow-hidden rounded-3xl border border-emerald-100 bg-white p-6 sm:p-8 shadow-xl">
            
            {/* Header Badge */}
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-3xl bg-emerald-100 text-emerald-600 shadow-sm shadow-emerald-600/20">
                <FaCheckCircle className="h-8 w-8" />
              </div>
              <span className="rounded-full bg-emerald-100 px-3.5 py-1 text-xs font-bold text-emerald-800">
                Return Request Submitted
              </span>
              <h1 className="mt-3 text-2xl font-black text-gray-900 sm:text-3xl">
                We&apos;ve Received Your Return Request
              </h1>
              <p className="mt-1 text-xs text-gray-500">
                Return Reference: <span className="font-mono font-bold text-emerald-800">{retRef}</span> &nbsp;·&nbsp; Order #{retOrder}
              </p>
            </div>

            {/* Current Status Info Banner */}
            {statusInfo && (
              <div className="mt-6 rounded-2xl border border-amber-200/80 bg-amber-50/70 p-4 text-xs">
                <div className="flex items-center gap-2 font-extrabold text-amber-900">
                  <FaClock className="h-4 w-4 text-amber-600 shrink-0" />
                  <span>{statusInfo.title}</span>
                </div>
                <p className="mt-1 font-semibold text-amber-900">{statusInfo.message}</p>
                {statusInfo.sub_message && (
                  <p className="mt-0.5 text-[11px] text-amber-700 font-medium">{statusInfo.sub_message}</p>
                )}
              </div>
            )}

            {/* Timeline Progress */}
            {timeline && (
              <div className="mt-6 rounded-2xl border border-gray-100 bg-gray-50/80 p-4 sm:p-5">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">
                  Return Progress Timeline
                </h3>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {Object.entries(timeline).map(([key, step]) => {
                    const isDone = step.status === "COMPLETED";
                    const isCurrent = step.status === "CURRENT";
                    return (
                      <div key={key} className="space-y-1">
                        <div className="flex items-center gap-1.5">
                          <span
                            className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                              isDone
                                ? "bg-emerald-600 text-white"
                                : isCurrent
                                ? "bg-amber-500 text-white animate-pulse"
                                : "bg-gray-200 text-gray-500"
                            }`}
                          >
                            {isDone ? "✓" : isCurrent ? "•" : "·"}
                          </span>
                          <span className={`text-[11px] font-bold truncate ${isCurrent ? "text-amber-800" : isDone ? "text-emerald-800" : "text-gray-400"}`}>
                            {step.title}
                          </span>
                        </div>
                        {step.date && (
                          <p className="pl-5 text-[10px] text-gray-400">
                            {step.date}{step.time ? ` · ${step.time}` : ""}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Scheduled Pickup & Refund Details Cards */}
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Pickup Details */}
              {pickup && (
                <div className="rounded-2xl border border-indigo-100 bg-indigo-50/40 p-4 text-xs">
                  <div className="flex items-center gap-2 font-extrabold text-indigo-900 mb-2">
                    <FaTruck className="h-4 w-4 text-indigo-600" />
                    <span>Scheduled Pickup</span>
                  </div>
                  <p className="font-bold text-gray-900">
                    {pickup.pickup_date} {pickup.pickup_time ? `· ${pickup.pickup_time}` : ""}
                  </p>
                  {pickup.pickup_address && (
                    <div className="mt-2 text-[11px] text-gray-600 border-t border-indigo-100/60 pt-2">
                      <p className="font-bold text-gray-800">{pickup.pickup_address.name}</p>
                      <p className="mt-0.5">
                        {pickup.pickup_address.address_line || ""}{pickup.pickup_address.city ? `, ${pickup.pickup_address.city}` : ""}{pickup.pickup_address.state ? `, ${pickup.pickup_address.state}` : ""} - {pickup.pickup_address.pincode}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Refund Information */}
              {refundInfo && (
                <div className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-4 text-xs">
                  <div className="flex items-center gap-2 font-extrabold text-emerald-900 mb-2">
                    <FaMoneyBillWave className="h-4 w-4 text-emerald-600" />
                    <span>Refund Details</span>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-gray-600 font-medium">Refund Amount:</span>
                    <span className="text-base font-black text-emerald-700">₹{parseFloat(refundInfo.refund_amount ?? 0).toFixed(2)}</span>
                  </div>
                  <div className="mt-2 text-[11px] text-gray-600 border-t border-emerald-100/60 pt-2 space-y-0.5">
                    <p>Method: <span className="font-semibold text-gray-800">{refundInfo.refund_method}</span></p>
                    {refundInfo.estimated_refund_time && (
                      <p>Estimated Time: <span className="font-semibold text-gray-800">{refundInfo.estimated_refund_time}</span></p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Returned Items Summary */}
            {retItems.length > 0 && (
              <div className="mt-6 rounded-2xl border border-gray-100 bg-gray-50/50 p-4">
                <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-3">
                  Items to be Returned
                </p>
                <div className="space-y-2">
                  {retItems.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 text-xs">
                      {item.image ? (
                        <img
                          src={buildAssetUrl(item.image)}
                          alt={item.product_name}
                          className="h-10 w-10 rounded-xl object-cover border border-gray-200"
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-200 text-gray-500">
                          <FaBoxOpen className="h-5 w-5" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="font-bold text-gray-900 truncate">{item.product_name}</p>
                        <p className="text-[11px] text-gray-500">Quantity: {item.quantity}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => navigate("/returns")}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 py-3.5 text-sm font-bold text-white shadow-md shadow-emerald-600/20 hover:bg-emerald-700 transition active:scale-95"
              >
                View in Returns &amp; Refunds
              </button>
              <button
                type="button"
                onClick={() => navigate("/account/orders")}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white py-3.5 text-sm font-bold text-gray-700 hover:bg-gray-50 transition active:scale-95"
              >
                Back to My Orders
              </button>
            </div>

          </div>
        </div>
      </div>
    );
  }

  const orderId = resolvedOrder.order_id ?? resolvedOrder.id;
  const orderNumber = resolvedOrder.order_number ?? `#${orderId}`;
  const placedAt = resolvedOrder.placed_at ?? resolvedOrder.created_at;
  const items = resolvedOrder.items ?? [];

  return (
    <div className="min-h-screen bg-gray-50/70 pb-20 pt-6">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        
        {/* ── Top Bar with Back Button & Breadcrumbs ── */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm font-bold text-gray-700 shadow-xs transition hover:bg-gray-100 hover:text-gray-900 active:scale-95"
          >
            <FaArrowLeft className="h-3.5 w-3.5" />
            Back to Orders
          </button>

          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-800">
              <FaUndo className="h-3 w-3" />
              Return &amp; Refund Request
            </span>
          </div>
        </div>

        {/* ── Page Title & Full Delivery Address Card ── */}
        <div className="mb-8 rounded-3xl border border-gray-200/80 bg-white p-6 shadow-sm sm:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-black tracking-tight text-gray-900 sm:text-3xl">
                Request Return &amp; Refund
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Select the delivered item(s) you wish to return for a direct refund.
              </p>
            </div>

            {/* Order Number & Placed Date Badge */}
            <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-gray-100 bg-gray-50/90 p-4 shrink-0">
              <div className="min-w-0">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Order Number</span>
                <p className="font-mono text-base font-extrabold text-gray-900">{orderNumber}</p>
              </div>

              {placedAt && (
                <div className="border-l border-gray-200 pl-3">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Placed On</span>
                  <p className="flex items-center gap-1 text-xs font-semibold text-gray-700">
                    <FaCalendarAlt className="h-3 w-3 text-gray-400" />
                    {new Date(placedAt).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* ── FULL DELIVERY & PICKUP ADDRESS BANNER ── */}
          <div className="mt-6 rounded-2xl border border-emerald-200/80 bg-emerald-50/50 p-4 sm:p-5">
            <div className="flex items-start gap-3.5">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#046a38] text-white shadow-xs">
                <FaMapMarkerAlt className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2.5">
                  <span className="text-[11px] font-extrabold uppercase tracking-wider text-[#046a38]">
                    Delivery &amp; Pickup Address
                  </span>
                  {addressData.name && (
                    <span className="rounded-md bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-900">
                      {addressData.name}
                    </span>
                  )}
                  {addressData.mobile && (
                    <span className="inline-flex items-center gap-1 rounded-md bg-white border border-emerald-200 px-2 py-0.5 text-xs font-semibold text-gray-700">
                      <FaPhoneAlt className="h-2.5 w-2.5 text-emerald-600" />
                      {addressData.mobile}
                    </span>
                  )}
                </div>

                <p className="mt-2 text-sm font-semibold leading-relaxed text-gray-900">
                  {addressData.fullText}
                </p>
                <p className="mt-1 text-[11px] text-gray-500">
                  Our delivery executive will pick up the returned products from this address.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Main 2-Column Grid ── */}
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">

            {/* ── Left 2 Columns: Return Form Content ── */}
            <div className="space-y-6 lg:col-span-2">

              {/* 1. Item Selection Section */}
              <div className="overflow-hidden rounded-3xl border border-gray-200/80 bg-white p-6 shadow-sm">
                <div className="flex items-center justify-between border-b border-gray-100 pb-4">
                  <div>
                    <h2 className="text-base font-extrabold text-gray-900">
                      1. Select Products to Return
                    </h2>
                    <p className="text-xs text-gray-500">
                      Check the item(s) you wish to return and adjust the quantity.
                    </p>
                  </div>
                  <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-800">
                    {selectedCount} of {items.length} selected
                  </span>
                </div>

                <div className="mt-4 space-y-4">
                  {items.map((item) => {
                    const itId = String(item.order_item_id ?? item.id);
                    const isChecked = Boolean(selectedItems[itId]);
                    const maxQty = parseInt(item.quantity ?? item.qty ?? 1, 10);
                    const currentQty = Number(itemQuantities[itId] ?? 1);
                    const price = parseFloat(item.unit_price ?? item.price ?? item.selling_price ?? 0);
                    const itemName = item.product_name ?? item.name ?? "Fresh Produce";
                    const itemImg = item.product_image ?? item.image;
                    const variant = item.variant_name ?? item.variant ?? "";

                    return (
                      <div
                        key={itId}
                        className={`relative rounded-2xl border p-4 transition-all duration-200 ${
                          isChecked
                            ? "border-emerald-500 bg-emerald-50/20 shadow-xs"
                            : "border-gray-200 bg-white opacity-80 hover:border-gray-300 hover:opacity-100"
                        }`}
                      >
                        <div className="flex items-start gap-3.5">
                          {/* Checkbox */}
                          <button
                            type="button"
                            onClick={() => handleToggleItem(itId)}
                            className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border-2 transition-all active:scale-95"
                            style={{
                              borderColor: isChecked ? "#046a38" : "#d1d5db",
                              backgroundColor: isChecked ? "#046a38" : "#ffffff",
                            }}
                            title={isChecked ? "Deselect item" : "Select item"}
                          >
                            {isChecked && <FaCheckCircle className="h-4 w-4 text-white" />}
                          </button>

                          {/* Product Image */}
                          <ProductThumb src={itemImg} alt={itemName} />

                          {/* Info */}
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-baseline justify-between gap-2">
                              <h3 className="text-sm font-bold text-gray-900">{itemName}</h3>
                              <p className="text-sm font-extrabold text-emerald-700">
                                ₹{(price * currentQty).toFixed(2)}
                              </p>
                            </div>

                            <p className="mt-0.5 text-xs text-gray-500">
                              {variant ? `${variant} · ` : ""}
                              Purchased: {maxQty} {maxQty > 1 ? "units" : "unit"} · ₹{price.toFixed(2)} each
                            </p>

                            {/* Quantity Selector & Issue Remark (when checked) */}
                            {isChecked && (
                              <div className="mt-3.5 space-y-2.5 border-t border-emerald-100/70 pt-3">
                                <div className="flex flex-wrap items-center gap-3">
                                  <span className="text-xs font-bold text-gray-700">Return Qty:</span>
                                  <div className="inline-flex items-center rounded-xl border border-gray-200 bg-white shadow-2xs">
                                    <button
                                      type="button"
                                      disabled={currentQty <= 1}
                                      onClick={() => handleQtyChange(itId, maxQty, -1)}
                                      className="flex h-8 w-8 items-center justify-center text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed rounded-l-xl transition"
                                    >
                                      <FaMinus className="h-2.5 w-2.5" />
                                    </button>
                                    <span className="w-10 text-center text-xs font-black text-gray-900">
                                      {currentQty}
                                    </span>
                                    <button
                                      type="button"
                                      disabled={currentQty >= maxQty}
                                      onClick={() => handleQtyChange(itId, maxQty, 1)}
                                      className="flex h-8 w-8 items-center justify-center text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed rounded-r-xl transition"
                                    >
                                      <FaPlus className="h-2.5 w-2.5" />
                                    </button>
                                  </div>
                                  <span className="text-[11px] text-gray-400">
                                    (Max: {maxQty})
                                  </span>
                                </div>

                                {/* Per-item issue note */}
                                <div>
                                  <input
                                    type="text"
                                    value={itemRemarks[itId] ?? ""}
                                    onChange={(e) => handleRemarkChange(itId, e.target.value)}
                                    placeholder="Issue with this product (optional, e.g., pack torn, stale)"
                                    className="w-full rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-800 placeholder-gray-400 outline-none transition focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                                    maxLength={200}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 2. Resolution: Return & 100% Refund Guarantee (Replacement and Credit removed) */}
              <div className="rounded-3xl border border-emerald-200/80 bg-gradient-to-br from-emerald-50/70 via-white to-emerald-50/30 p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-600 text-white shadow-sm shadow-emerald-600/20">
                    <FaMoneyBillWave className="h-6 w-6" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-base font-extrabold text-gray-900">
                        2. Resolution: Return &amp; Refund
                      </h2>
                      <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[10px] font-bold text-emerald-800">
                        Direct Refund
                      </span>
                    </div>
                    <p className="mt-1 text-xs leading-relaxed text-gray-600">
                      Once your returned item is picked up and verified by our team, the approved refund will be credited directly back to your original payment source (or bank account for Cash on Delivery orders).
                    </p>
                  </div>
                </div>
              </div>

              {/* 3. Reason for Return */}
              <div className="rounded-3xl border border-gray-200/80 bg-white p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <h2 className="text-base font-extrabold text-gray-900">
                    3. Reason(s) for Return <span className="text-red-500">*</span>
                  </h2>
                  <span className="text-[11px] text-gray-400">Select all that apply</span>
                </div>
                <p className="text-xs text-gray-500">
                  Tell us why you are returning these item(s) so we can improve quality.
                </p>

                <div className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  {REASON_CODE_ENTRIES.map(([code, label]) => {
                    const isChecked = selectedReasons.includes(code);

                    return (
                      <button
                        key={code}
                        type="button"
                        onClick={() => toggleReason(code)}
                        className={`flex items-center gap-3 rounded-2xl border p-3.5 text-left transition-all active:scale-98 ${
                          isChecked
                            ? "border-emerald-500 bg-emerald-50/70 text-emerald-900 shadow-2xs font-bold"
                            : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 font-semibold"
                        }`}
                      >
                        <div
                          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-lg border-2 transition ${
                            isChecked ? "border-emerald-600 bg-emerald-600" : "border-gray-300 bg-white"
                          }`}
                        >
                          {isChecked && <FaCheckCircle className="h-3.5 w-3.5 text-white" />}
                        </div>
                        <span className="text-xs">{label}</span>
                      </button>
                    );
                  })}
                </div>
                {selectedReasons.includes("OTHER") && (
                  <div className="mt-4">
                    <label htmlFor="other-return-reason" className="mb-1 block text-xs font-bold text-gray-700">
                      Please describe the issue <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="other-return-reason"
                      value={otherReasonNote}
                      onChange={(e) => setOtherReasonNote(e.target.value.slice(0, MAX_OTHER_REASON_LENGTH))}
                      minLength={5}
                      maxLength={MAX_OTHER_REASON_LENGTH}
                      rows={3}
                      placeholder="Tell us about your reason or complaint (5–500 characters)"
                      className="w-full resize-y rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-800 outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                    />
                    <p className="mt-1 text-right text-[10px] text-gray-400">
                      {otherReasonNote.length}/{MAX_OTHER_REASON_LENGTH}
                    </p>
                  </div>
                )}
              </div>

              {/* 4. Refund Method */}
              <div className="rounded-3xl border border-gray-200/80 bg-white p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <h2 className="text-base font-extrabold text-gray-900">
                    4. Refund Method <span className="text-red-500">*</span>
                  </h2>
                  <span className="text-[11px] text-gray-400">Choose refund destination</span>
                </div>
                <p className="text-xs text-gray-500">
                  Select where you would like to receive your refund once the return is picked up and verified.
                </p>

                <div className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-3">
                  {REFUND_PREFERENCE_OPTIONS.map((opt) => {
                    const isSelected = refundPreference === opt.value;
                    return (
                      <label
                        key={opt.value}
                        className={`flex cursor-pointer flex-col justify-between rounded-2xl border p-4 transition-all ${
                          isSelected
                            ? "border-emerald-500 bg-emerald-50/70 shadow-xs ring-1 ring-emerald-500"
                            : "border-gray-200 bg-white hover:border-gray-300"
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <span className={`text-xs font-bold ${isSelected ? "text-emerald-900" : "text-gray-900"}`}>
                            {opt.label}
                          </span>
                          <input
                            type="radio"
                            name="refund_preference"
                            value={opt.value}
                            checked={isSelected}
                            onChange={() => setRefundPreference(opt.value)}
                            className="h-4 w-4 accent-emerald-600 mt-0.5"
                          />
                        </div>
                        <p className="mt-2 text-[11px] text-gray-500 leading-relaxed">
                          {opt.description}
                        </p>
                      </label>
                    );
                  })}
                </div>

                {/* Bank Transfer Details */}
                {refundPreference === "BANK_TRANSFER" && (
                  <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50/40 p-4 space-y-3">
                    <p className="text-xs font-bold uppercase tracking-wider text-emerald-900">
                      Bank Account Details
                    </p>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div>
                        <label className="block text-[11px] font-bold text-gray-700 mb-1">
                          Account Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={bankAccountNumber}
                          onChange={(e) => setBankAccountNumber(e.target.value.replace(/\D/g, ""))}
                          minLength={1}
                          maxLength={MAX_BANK_ACCOUNT_NUMBER_LENGTH}
                          inputMode="numeric"
                          pattern="[0-9]*"
                          placeholder="e.g. 123456789012"
                          className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-800 outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-gray-700 mb-1">
                          IFSC Code <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={bankIfscCode}
                          onChange={(e) => setBankIfscCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                          placeholder="e.g. HDFC0000123"
                          className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs uppercase text-gray-800 outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                          minLength={MAX_BANK_IFSC_CODE_LENGTH}
                          maxLength={MAX_BANK_IFSC_CODE_LENGTH}
                          pattern="[A-Za-z]{4}0[A-Za-z0-9]{6}"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-gray-700 mb-1">
                          Account Holder Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={bankAccountHolder}
                          onChange={(e) => setBankAccountHolder(e.target.value)}
                          minLength={2}
                          maxLength={MAX_BANK_ACCOUNT_HOLDER_LENGTH}
                          placeholder="Name as per bank records"
                          className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-800 outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-gray-700 mb-1">
                          Bank Name (Optional)
                        </label>
                        <input
                          type="text"
                          value={bankName}
                          onChange={(e) => setBankName(e.target.value)}
                          minLength={2}
                          maxLength={MAX_BANK_NAME_LENGTH}
                          placeholder="e.g. HDFC Bank"
                          className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-800 outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* UPI ID Details */}
                {refundPreference === "UPI" && (
                  <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50/40 p-4 space-y-2">
                    <label className="block text-xs font-bold text-emerald-900">
                      UPI ID / VPA <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={upiId}
                      onChange={(e) => setUpiId(e.target.value.slice(0, MAX_UPI_ID_LENGTH))}
                      minLength={MIN_UPI_ID_LENGTH}
                      maxLength={MAX_UPI_ID_LENGTH}
                      autoCapitalize="none"
                      autoCorrect="off"
                      placeholder="e.g. yourname@oksbi / username@paytm"
                      className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-800 outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                    />
                    <p className="text-[10px] text-gray-500">
                      Please double-check your UPI ID for instant and accurate credit.
                    </p>
                  </div>
                )}
              </div>

              {/* 5. Photos & Additional Notes */}
              <div className="rounded-3xl border border-gray-200/80 bg-white p-6 shadow-sm">
                <h2 className="text-base font-extrabold text-gray-900">
                  5. Attach Photos &amp; Details (Optional)
                </h2>
                <p className="text-xs text-gray-500">
                  Photos help our inspection team verify and speed up your refund approval.
                </p>

                {/* Upload Section */}
                <div className="mt-4">
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                    Damage / Product Photos ({previews.length}/5)
                  </label>

                  {previews.length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-3">
                      {previews.map((src, idx) => (
                        <div key={idx} className="group relative">
                          <img
                            src={src}
                            alt={`preview-${idx}`}
                            className="h-20 w-20 rounded-2xl object-cover border border-gray-200 shadow-xs"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(idx)}
                            className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white shadow-md hover:bg-red-600 transition"
                            title="Remove photo"
                          >
                            <FaTrashAlt className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {previews.length < 5 && (
                    <>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={handleImageChange}
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="flex w-full items-center justify-center gap-2.5 rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50/50 py-4 text-xs font-bold text-gray-600 transition hover:border-emerald-500 hover:bg-emerald-50/40 hover:text-emerald-700"
                      >
                        <FaCamera className="h-4 w-4 text-gray-400" />
                        {previews.length === 0 ? "Click to Upload Damage / Quality Photos" : "Add More Photos"}
                      </button>
                    </>
                  )}
                </div>

                {/* Additional Notes Textarea */}
                <div className="mt-5">
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">
                    Additional Comments
                  </label>
                  <textarea
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Provide any additional context (e.g. package arrived crushed, seal broken)..."
                    className="w-full resize-none rounded-2xl border border-gray-200 px-4 py-3 text-xs text-gray-800 placeholder-gray-400 outline-none transition focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                    maxLength={500}
                  />
                  <div className="mt-1 flex justify-between text-[11px] text-gray-400">
                    <span>Be descriptive for faster turnaround</span>
                    <span>{notes.length}/500</span>
                  </div>
                </div>
              </div>

            </div>

            {/* ── Right 1 Column: Summary Sidebar (Sticky) ── */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-5">
                
                {/* Summary Card */}
                <div className="overflow-hidden rounded-3xl border border-gray-200/80 bg-white p-6 shadow-sm">
                  <h3 className="text-base font-extrabold text-gray-900 border-b border-gray-100 pb-3">
                    Return &amp; Refund Summary
                  </h3>

                  <div className="mt-4 space-y-3 text-xs">
                    <div className="flex justify-between text-gray-600">
                      <span>Order Reference</span>
                      <span className="font-mono font-bold text-gray-900">{orderNumber}</span>
                    </div>

                    <div className="flex justify-between text-gray-600">
                      <span>Resolution</span>
                      <span className="font-bold text-emerald-700">Return &amp; Refund</span>
                    </div>

                    <div className="flex justify-between text-gray-600">
                      <span>Items to Return</span>
                      <span className="font-bold text-gray-900">{selectedCount} items</span>
                    </div>

                    <div className="flex justify-between text-gray-600">
                      <span>Refund Method</span>
                      <span className="font-bold text-gray-900">
                        {refundPreference === "WALLET"
                          ? "Wallet"
                          : refundPreference === "BANK_TRANSFER"
                          ? "Bank Transfer"
                          : "UPI"}
                      </span>
                    </div>

                    {/* Refund Estimate */}
                    <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
                      <div className="flex items-baseline justify-between">
                        <span className="text-xs font-bold text-emerald-900">Estimated Refund</span>
                        <span className="text-xl font-black text-emerald-700">
                          ₹{estimatedRefund.toFixed(2)}
                        </span>
                      </div>
                      <p className="mt-1 text-[10px] text-emerald-700/80">
                        {refundPreference === "WALLET"
                          ? "Instant credit to your KisanFresh wallet upon return pickup."
                          : refundPreference === "BANK_TRANSFER"
                          ? "Transferred to your bank account within 2-3 business days."
                          : "Transferred directly to your UPI ID upon verification."}
                      </p>
                    </div>

                    {/* Address snippet in sidebar */}
                    {addressData.fullText && (
                      <div className="mt-3 rounded-xl bg-gray-50 p-3 text-[11px] text-gray-600">
                        <div className="flex items-center gap-1 font-bold text-gray-700 mb-1">
                          <FaMapMarkerAlt className="h-3 w-3 text-emerald-600" />
                          <span>Pickup Address</span>
                        </div>
                        <p className="line-clamp-2">{addressData.fullText}</p>
                      </div>
                    )}
                  </div>

                  {/* Submit Button */}
                  <div className="mt-6">
                    <button
                      type="submit"
                      disabled={submitting || !selectedCount || !selectedReasons.length}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 py-3.5 text-sm font-bold text-white shadow-lg shadow-emerald-600/25 transition hover:bg-emerald-700 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <>
                          <FaSpinner className="h-4 w-4 animate-spin" />
                          Submitting Request...
                        </>
                      ) : (
                        <>
                          <FaCheckCircle className="h-4 w-4" />
                          Submit Return &amp; Refund
                        </>
                      )}
                    </button>
                  </div>

                  {/* Cancellation / Back button */}
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="mt-2.5 w-full rounded-2xl py-2.5 text-center text-xs font-semibold text-gray-500 hover:text-gray-700 transition"
                  >
                    Cancel and Return to Orders
                  </button>
                </div>

                {/* Assurance Card */}
                <div className="rounded-3xl border border-emerald-100 bg-gradient-to-br from-emerald-50/60 to-white p-5 text-xs text-gray-600">
                  <div className="flex items-center gap-2 font-bold text-emerald-800">
                    <FaShieldAlt className="h-4 w-4 text-emerald-600 shrink-0" />
                    <span>Kisan Fresh Quality Guarantee</span>
                  </div>
                  <p className="mt-1.5 leading-relaxed text-[11px] text-gray-500">
                    100% Hassle-Free Returns. Approved refund requests are credited back to your original payment method promptly after item verification.
                  </p>
                </div>

              </div>
            </div>

          </div>
        </form>

      </div>
    </div>
  );
};

export default RequestReturnPage;
